import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Button } from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/pdfExport.css';

const formatNumber = (num) => (num !== undefined && num !== null ? Math.round(num) : 0);

const TablaUltimoAcumuladoRPM = () => {
    const [startDate, setStartDate] = useState(() => localStorage.getItem('tablaAcumuladoStartDate') || '');
    const [endDate, setEndDate] = useState(() => localStorage.getItem('tablaAcumuladoEndDate') || '');
    const [data, setData] = useState(() => {
        const saved = localStorage.getItem('tablaAcumuladoData');
        try {
            const parsed = saved ? JSON.parse(saved) : { machineIds: [], results: [] };
            return parsed && Array.isArray(parsed.results) ? parsed : { machineIds: [], results: [] };
        } catch {
            return { machineIds: [], results: [] };
        }
    });
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        try {
            const response = await fetch(`/api/ultimo-acumulado-rpm?startDate=${startDate}&endDate=${endDate}`);
            const result = await response.json();
            const newData = {
                machineIds: result.machineIds || [],
                results: Array.isArray(result.resultado) ? result.resultado : (result.results || [])
            };
            setData(newData);
            localStorage.setItem('tablaAcumuladoData', JSON.stringify(newData));
            localStorage.setItem('tablaAcumuladoStartDate', startDate);
            localStorage.setItem('tablaAcumuladoEndDate', endDate);
        } catch (error) {
            setData({ machineIds: [], results: [] });
        }
        setLoading(false);
    };

    useEffect(() => {
        if (startDate && endDate) {
            fetchData();
        }
        // eslint-disable-next-line
    }, [startDate, endDate]);

    // Función para descargar la tabla como PDF
    const descargarPDF = () => {
        const input = document.getElementById('tabla-acumulado-rpm');
        if (!input) return;
        input.classList.add('pdf-export');
        
        html2canvas(input, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const marginX = 10;
            const marginY = 15;
            const imgWidth = pdfWidth - (marginX * 2);
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            const pageContentHeight = pdfHeight - (marginY * 2);
            
            let heightLeft = imgHeight;
            let position = marginY;
            
            pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
            heightLeft -= pageContentHeight;
            
            while (heightLeft > 0) {
                position = -(imgHeight - heightLeft) + marginY;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', marginX, position, imgWidth, imgHeight);
                heightLeft -= pageContentHeight;
            }
            
            pdf.save('ultimo-acumulado-rpm.pdf');
            input.classList.remove('pdf-export');
        });
    };

    // Calcular totales por columna (máquina)
    const totalesColumna = data.machineIds.map(id =>
        data.results.reduce((sum, row) => sum + formatNumber(row[id]), 0)
    );

    // Calcular totales por fila (día)
    const totalesFila = data.results.map(row =>
        data.machineIds.reduce((sum, id) => sum + formatNumber(row[id]), 0)
    );

    return (
        <Container>
            <Typography variant="h4" align="center"  mb={6} mt={6}  gutterBottom>
                Último Acumulado RPM por Día y Máquina
            </Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="outlined" color="secondary" onClick={descargarPDF}>
                    Descargar PDF
                </Button>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <TextField
                    label="Fecha inicio"
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        '& .MuiInputBase-root': {
                            backgroundColor: '#302f2fff'
                        }
                    }}
                />
                <TextField
                    label="Fecha fin"
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        '& .MuiInputBase-root': {
                            backgroundColor: '#302f2fff'
                        }
                    }}
                />
                <Button variant="contained" color="primary" onClick={fetchData}>
                    Filtrar
                </Button>
            </Box>
            <Box id="tabla-acumulado-rpm">
                
                <TableContainer component={Paper} sx={{ maxHeight: '600px' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#313131', color: 'white' }}>Fecha</TableCell>
                                {data.machineIds.map((id, idx) => (
                                    <TableCell key={idx} sx={{ fontWeight: 'bold', backgroundColor: '#313131', color: 'white' }}>{id}</TableCell>
                                ))}
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#313131', color: 'white' }}>Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={data.machineIds.length + 2} align="center" sx={{ color: '#000' }}>
                                        Cargando...
                                    </TableCell>
                                </TableRow>
                            ) : data.results.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={data.machineIds.length + 2} align="center" sx={{ color: '#000' }}>
                                        No hay datos para los filtros seleccionados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {data.results.map((row, idx) => (
                                        <TableRow key={row.fecha || idx} sx={{ '&:hover': { backgroundColor: '#424242' } }}>
                                            <TableCell>{row.fecha}</TableCell>
                                            {data.machineIds.map(id => (
                                                <TableCell key={id}>
                                                    {formatNumber(row[id])}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={{ color: '#e6e3e3', fontWeight: 'bold' }}>
                                                <b>{totalesFila[idx]}</b>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Fila de totales por máquina */}
                                    <TableRow sx={{ backgroundColor: '#313131' }}>
                                        <TableCell sx={{ color: '#e6e3e3', fontWeight: 'bold' }}>
                                            <b>Total</b>
                                        </TableCell>
                                        {totalesColumna.map((total, idx) => (
                                            <TableCell key={idx} sx={{ color: '#e6e3e3', fontWeight: 'bold' }}>
                                                <b>{total}</b>
                                            </TableCell>
                                        ))}
                                        <TableCell></TableCell>
                                    </TableRow>
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Container>
    );
};

export default TablaUltimoAcumuladoRPM;